import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import { Header } from 'semantic-ui-react'
import styled from 'styled-components'
import { CoolFarmCropResultsType } from '~/api/services/coolFarmCrop'

interface BarChartProps {
  results: CoolFarmCropResultsType
}

const Segment = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
`

const safeParseFloat = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return 0
  return parseFloat(String(value))
}

const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k'
  }
  return num.toFixed(2)
}

const formatTooltipValue = (value: number) => {
  return formatNumber(value)
}

const BarChart: React.FC<BarChartProps> = ({ results }) => {
  const { t } = useTranslation()

  const chartData = useMemo(() => {
    return [
      {
        name: t('Seed production'),
        value: 0,
        co2: 0,
        n2o: 0,
        ch4: 0,
        total: 0,
        perHa: 0,
        perKg: 0
      },
      {
        name: t('Residue management'),
        value: safeParseFloat(results.residueTotalEmission),
        co2: safeParseFloat(results.residueCo2Emission),
        n2o: safeParseFloat(results.residueN2oEmission),
        ch4: safeParseFloat(results.residueCh4Emission),
        total: safeParseFloat(results.residueTotalEmission),
        perHa: safeParseFloat(results.residueTotalEmissionPerArea),
        perKg: safeParseFloat(results.residueTotalEmissionPerProduct)
      },
      {
        name: t('Fertiliser production*'),
        value: safeParseFloat(results.fertiliserProductionTotalEmission),
        co2: safeParseFloat(results.fertiliserProductionCo2Emission),
        n2o: safeParseFloat(results.fertiliserProductionN2oEmission),
        ch4: safeParseFloat(results.fertiliserProductionCh4Emission),
        total: safeParseFloat(results.fertiliserProductionTotalEmission),
        perHa: safeParseFloat(results.fertiliserProductionTotalEmissionPerArea),
        perKg: safeParseFloat(results.fertiliserProductionTotalEmissionPerProduct)
      },
      {
        name: t('Soil / fertiliser'),
        value: safeParseFloat(results.fertiliserSoilTotalEmission),
        co2: safeParseFloat(results.fertiliserSoilCo2Emission),
        n2o: safeParseFloat(results.fertiliserSoilN2oEmission),
        ch4: safeParseFloat(results.fertiliserSoilCh4Emission),
        total: safeParseFloat(results.fertiliserSoilTotalEmission),
        perHa: safeParseFloat(results.fertiliserSoilTotalEmissionPerArea),
        perKg: safeParseFloat(results.fertiliserSoilTotalEmissionPerProduct)
      },
      {
        name: t('Paddy methane'),
        value: 0,
        co2: 0,
        n2o: 0,
        ch4: 0,
        total: 0,
        perHa: 0,
        perKg: 0
      },
      {
        name: t('Crop protection'),
        value: safeParseFloat(results.pesticideTotalEmission),
        co2: safeParseFloat(results.pesticideCo2Emission),
        n2o: safeParseFloat(results.pesticideN2oEmission),
        ch4: safeParseFloat(results.pesticideCh4Emission),
        total: safeParseFloat(results.pesticideTotalEmission),
        perHa: safeParseFloat(results.pesticideTotalEmissionPerArea),
        perKg: safeParseFloat(results.pesticideTotalEmissionPerProduct)
      },
      {
        name: t('Carbon stock changes'),
        value: safeParseFloat(results.managementChangesTotalEmission),
        co2: safeParseFloat(results.managementChangesCo2Emission),
        n2o: safeParseFloat(results.managementChangesN2oEmission),
        ch4: safeParseFloat(results.managementChangesCh4Emission),
        total: safeParseFloat(results.managementChangesTotalEmission),
        perHa: safeParseFloat(results.managementChangesTotalEmissionPerArea),
        perKg: safeParseFloat(results.managementChangesTotalEmissionPerProduct)
      },
      {
        name: t('Energy use (field)'),
        value: safeParseFloat(results.fieldEnergyTotalEmission),
        co2: safeParseFloat(results.fieldEnergyCo2Emission),
        n2o: safeParseFloat(results.fieldEnergyN2oEmission),
        ch4: safeParseFloat(results.fieldEnergyCh4Emission),
        total: safeParseFloat(results.fieldEnergyTotalEmission),
        perHa: safeParseFloat(results.fieldEnergyTotalEmissionPerArea),
        perKg: safeParseFloat(results.fieldEnergyTotalEmissionPerProduct)
      },
      {
        name: t('Energy use (processing)'),
        value: safeParseFloat(results.machineryTotalEmission),
        co2: safeParseFloat(results.machineryCo2Emission),
        n2o: safeParseFloat(results.machineryN2oEmission),
        ch4: safeParseFloat(results.machineryCh4Emission),
        total: safeParseFloat(results.machineryTotalEmission),
        perHa: safeParseFloat(results.machineryTotalEmissionPerArea),
        perKg: safeParseFloat(results.machineryTotalEmissionPerProduct)
      },
      {
        name: t('Waste water'),
        value: safeParseFloat(results.wasteWaterTotalEmission),
        co2: safeParseFloat(results.wasteWaterCo2Emission),
        n2o: safeParseFloat(results.wasteWaterN2oEmission),
        ch4: safeParseFloat(results.wasteWaterCh4Emission),
        total: safeParseFloat(results.wasteWaterTotalEmission),
        perHa: safeParseFloat(results.wasteWaterTotalEmissionPerArea),
        perKg: safeParseFloat(results.wasteWaterTotalEmissionPerProduct)
      },
      {
        name: t('Off-farm transport'),
        value: safeParseFloat(results.transportationTotalEmission),
        co2: safeParseFloat(results.transportationCo2Emission),
        n2o: safeParseFloat(results.transportationN2oEmission),
        ch4: safeParseFloat(results.transportationCh4Emission),
        total: safeParseFloat(results.transportationTotalEmission),
        perHa: safeParseFloat(results.transportationTotalEmissionPerArea),
        perKg: safeParseFloat(results.transportationTotalEmissionPerProduct)
      }
    ]
  }, [results, t])

  return (
    <Segment>
      <Header as='h3' textAlign='center' style={{ marginBottom: '20px' }}>
        {t('Total Emissions (kg CO₂e)')}
      </Header>
      <ResponsiveContainer width='100%' height={400}>
        <RechartsBarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey='name'
            angle={-45}
            textAnchor='end'
            height={120}
            interval={0}
          />
          <YAxis
            tickFormatter={formatNumber}
          />
          <Tooltip
            formatter={formatTooltipValue}
            labelFormatter={(label) => `Source: ${label}`}
          />
          <Bar dataKey='value' fill='#8884d8' />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Segment>
  )
}

export default BarChart
